import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Box, Stack } from '@mui/system';

import AssignProductsTable from '@/features/Shipping/components/AssignProductsTable/AssignProductsTable';
import ModalNotification, {
  ModalNotificationProps,
} from '@/shared/components/dialogs/ModalNotification';
import ProductVariant from '@/shared/types/products/productVariant.type';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';
import ShippingProvider from '@/shared/types/shipping/shippingProvider.type';

import CustomsDetails from '../../../components/CustomsDetails/CustomsDetails';
import ShippingProfileRequestBody from '../../types/createShippingProfileRequestBody.type';
import AddFirstZone from './components/AddFirstZone/AddFirstZone';
import ProfileDetails from './components/ProfileDetails/ProfileDetails';
import SetupOptions from './components/SetupOptions/SetupOptions';
import useCreateEditShippingProfileDialog, {
  viewId,
} from './hooks/useCreateEditShippingProfileDialog';
import getRequestBody from './utils/getRequestBody';

type CreateEditShippingProfileDialogProps = {
  shippingProviders: ShippingProvider[];
  templateSchemas: ProfilesTemplateSchemas;
  initialView?: keyof typeof viewId;
  errors?: string[];
  onClose: () => void;
  proceed: (value: ShippingProfileRequestBody) => void;
};

const CreateEditShippingProfileDialog: FC<
  CreateEditShippingProfileDialogProps
> = ({
  shippingProviders,
  templateSchemas,
  initialView,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const [tableDetails, setTableDetails] = useState({
    rows: [] as ProductVariant[],
    level: 'products',
  });

  const {
    setupOptionsState,
    profileDetailsState,
    addFirstZoneState,
    customsDetailsState,
    providerPaymentMethods,
    currentView,
    showNextView,
    showPrevView,
    getView,
    setCurrentView,
  } = useCreateEditShippingProfileDialog({ errors, initialView });

  const requestBody = getRequestBody({
    setupOptionsState,
    profileDetailsState,
    addFirstZoneState,
    customsDetailsState,
    tableSelectedRows: tableDetails.rows,
  });

  const getActions = (view: keyof typeof viewId) => {
    const { nextView } = getView(currentView);

    let actions: ModalNotificationProps['dialogProps']['actions'] = [
      {
        label: t('common:back'),
        variant: 'text',
        color: 'primary',
        onClick: showPrevView,
      },
      {
        label: !nextView ? t('shipping:addProfile') : t('common:next'),
        variant: 'contained',
        color: 'primary',
        onClick: () => {
          if (!nextView) {
            return proceed(requestBody);
          }
          showNextView();
        },
      },
    ];

    if (view === 'setup_options') {
      actions.shift();
    } else if (view === 'error') {
      actions = [
        {
          label: t('shipping:checkTheForm'),
          variant: 'text',
          color: 'primary',
          onClick: () => setCurrentView('setup_options'),
        },
        {
          label: t('common:close'),
          variant: 'text',
          color: 'primary',
          onClick: onClose,
        },
      ];
    } else if (
      view === 'product_variants' &&
      initialView === 'product_variants'
    ) {
      actions = [
        {
          label: 'action-button',
          renderComponent: !nextView ? (
            <Trans
              i18nKey={
                tableDetails.level === 'products'
                  ? 'shipping:assignSelectedProducts'
                  : 'shipping:assignSelectedVariants'
              }
              ns='shipping'
              components={{ span: <Box component='span' sx={{ mr: 0.5 }} /> }}
              count={tableDetails.rows.length}
            />
          ) : (
            t('common:next')
          ),
          disabled: tableDetails.rows.length === 0,
          variant: 'contained',
          color: 'primary',
          onClick: () => {
            if (!nextView) {
              return proceed(requestBody);
            }
            showNextView();
          },
        },
      ];
    }

    return actions;
  };

  const viewsConfig: {
    [key in keyof typeof viewId]: ModalNotificationProps['dialogProps'];
  } = {
    setup_options: {
      title: `${t('shipping:addProfile')}: ${t('shipping:setupOptions')}`,
      content: (
        <SetupOptions
          setupOptionsState={setupOptionsState}
          shippingProviders={shippingProviders}
        />
      ),
      actions: getActions('setup_options'),
    },
    profile_details: {
      title: `${t('shipping:addProfile')}: ${t('shipping:profileDetails')}`,
      content: (
        <ProfileDetails
          profileDetailsState={profileDetailsState}
          templateSchemas={templateSchemas}
          providerPaymentMethods={providerPaymentMethods}
        />
      ),
      actions: getActions('profile_details'),
    },
    add_zone: {
      title: `${t('shipping:addProfile')}: ${t('shipping:addFirstZone')}`,
      content: (
        <AddFirstZone
          addFirstZoneState={addFirstZoneState}
          templateSchemas={templateSchemas}
        />
      ),
      actions: getActions('add_zone'),
    },
    customs_details: {
      title: `${t('shipping:addProfile')}: ${t('shipping:customsDetails')}`,
      content: (
        <CustomsDetails
          templateSchemas={templateSchemas}
          customsDetailsState={customsDetailsState}
        />
      ),
      actions: getActions('customs_details'),
    },
    product_variants: {
      title: `${t('shipping:addProfile')}: ${t('shipping:assignProductsAndVariants')}`,
      content: (
        <AssignProductsTable
          getSelectedRows={(rows) =>
            setTableDetails((prev) => ({ ...prev, rows }))
          }
          getSelectedLevel={(level) =>
            setTableDetails((prev) => ({ ...prev, level }))
          }
        />
      ),
      actions: getActions('product_variants'),
    },
    error: {
      title: t('shipping:addProfile'),
      content: (
        <Stack direction='column' gap={2}>
          {errors?.map((error, index) => (
            <Alert key={index} severity='error'>
              {error}
            </Alert>
          ))}
        </Stack>
      ),
      actions: getActions('error'),
    },
  };

  return (
    <ModalNotification
      fullWidth
      size={currentView === 'product_variants' ? 'lg' : 'sm'}
      open
      onClose={onClose}
      dialogProps={viewsConfig[currentView]}
    />
  );
};

export default CreateEditShippingProfileDialog;
